'use client';

import { CiDeliveryTruck } from 'react-icons/ci';
import { ReactComponent as Check } from '@assets/icons/check.svg';
import { ReactComponent as ChevronDown } from '@assets/icons/chevron-down.svg';
import { ReactComponent as HandHoldingBox } from '@assets/icons/hand-holding-box.svg';

import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList
} from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { CartItemOnlineFulfillmentEnum, UserFulfillmentMode } from 'types';

import { Text } from 'ui';

interface FulfillmentSelectorProps {
  isDisplayMenu: boolean;
  defaultMode: CartItemOnlineFulfillmentEnum;
  cartItemId?: number;
  onModeChange?: (mode: UserFulfillmentMode) => void;
  isReadOnly?: boolean;
}

const fulfillmentOptions = [
  {
    value: UserFulfillmentMode.Ship,
    label: 'Ship',
    mode: UserFulfillmentMode.Ship,
    originalEnum: CartItemOnlineFulfillmentEnum.ShippingOnly
  },
  {
    value: UserFulfillmentMode.Pickup,
    label: 'Pick Up',
    mode: UserFulfillmentMode.Pickup,
    originalEnum: CartItemOnlineFulfillmentEnum.PickupOnly
  }
];

const FulfillmentSelector = ({
  defaultMode,
  isDisplayMenu = false,
  onModeChange,
  cartItemId,
  isReadOnly = false
}: FulfillmentSelectorProps) => {
  const { fulfillmentPreferences } = useAppSelector((state) => state.cart);

  const getSelectedMode = (): UserFulfillmentMode => {
    if (cartItemId && fulfillmentPreferences?.[cartItemId]) {
      return fulfillmentPreferences[cartItemId].user_selected;
    }
    switch (defaultMode) {
      case CartItemOnlineFulfillmentEnum.PickupOnly:
        return UserFulfillmentMode.Pickup;
      case CartItemOnlineFulfillmentEnum.ShippingOnly:
        return UserFulfillmentMode.Ship;
      case CartItemOnlineFulfillmentEnum.ShippingAndPickup:
      default:
        return UserFulfillmentMode.Ship;
    }
  };

  const selectedMode = getSelectedMode();

  const selectedOption = fulfillmentOptions.find(
    (option) => option.value === selectedMode
  );

  const isPickupMode = selectedOption?.mode === UserFulfillmentMode.Pickup;
  const itemLabel = isPickupMode ? 'Pick Up Only' : 'Ship Only';
  const formattedItemLabel = isDisplayMenu ? '' : itemLabel;

  const handleModeChange = (mode: UserFulfillmentMode) => {
    if (!isReadOnly) {
      onModeChange?.(mode);
    }
  };

  return (
    <Flex align='center' gap={2}>
      <Box
        px={2}
        py='6px'
        bg={isPickupMode ? 'red.400' : 'blue.main'}
        color='white'
        fontSize='12px'
        fontWeight='semibold'
        textTransform='uppercase'
        display='flex'
        alignItems='center'
        gap='6px'
        whiteSpace='nowrap'
        flexShrink={0}
        borderRadius='4px'
        height='31px'
      >
        {isPickupMode ? (
          <HandHoldingBox width='100%' height={16} fill='white' />
        ) : (
          <CiDeliveryTruck
            style={{
              height: '16px',
              width: '100%'
            }}
            fill='white'
          />
        )}
        {formattedItemLabel}
      </Box>

      <Box display={isDisplayMenu ? 'flex' : 'none'}>
        <Menu>
          {({ isOpen }) => (
            <>
              <MenuButton
                as={Button}
                variant='outline'
                size='sm'
                minW='130px'
                py={2}
                px={3}
                fontSize='12px'
                bg='white'
                border='1px solid'
                borderColor='gray.300'
                borderRadius='5px'
                isDisabled={isReadOnly}
                rightIcon={
                  <Box
                    as={ChevronDown}
                    width='16px'
                    height='16px'
                    transition='transform 0.2s ease'
                    transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                    fill='gray.600'
                  />
                }
                _hover={{ bg: 'gray.50' }}
                _active={{ bg: 'gray.100' }}
              >
                <Text fontSize='14px' color='gray.700'>
                  {selectedOption?.label}
                </Text>
              </MenuButton>

              <MenuList
                border='none'
                borderRadius='5px'
                p={0}
                minW='130px'
                boxShadow='md'
              >
                {fulfillmentOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => handleModeChange(option.value)}
                    px={3}
                    py={2}
                    fontSize='13px'
                    _hover={{ bg: 'primary.200' }}
                  >
                    <Flex align='center' justify='space-between' w='full'>
                      <Text fontSize='14px' fontWeight='medium'>
                        {option.label}
                      </Text>
                      {selectedMode === option.value && (
                        <Check width='14px' height='14px' />
                      )}
                    </Flex>
                  </MenuItem>
                ))}
              </MenuList>
            </>
          )}
        </Menu>
      </Box>
    </Flex>
  );
};

export default FulfillmentSelector;
